/**
 * 导航地图构建系统
 * 构建 Tab/Shift+Tab 的循环路径和方向键导航地图
 */

import { useMemo } from 'react';
import { UnifiedPosition } from './useUnifiedNavigationState';
import { SceneWithUserNames } from '../../../types/scene.types';
import { ElementWithUserNames, ElementType } from '../../../types/element.types';
import {
  rowsPerColumnFromTotalSlots,
  totalSlotsForElements,
} from '../../../utils/elementTableLayout';
import { sortElements } from '../../../utils/elementSortUtils';

/**
 * Tab 导航项
 */
export interface TabNavigationItem {
  position: UnifiedPosition;
  colKey?: string; // ScenesTable 单元格的列 key（用于特殊处理）
}

/**
 * SceneDetails 字段顺序
 */
export const SCENE_DETAILS_FIELD_ORDER = [
  'sceneInteriorExterior', // 内外
  'sceneDayNight',          // 日夜
  'sceneSet',               // 场景
  'scenePlotDay',           // 剧情日
  'scenePagesNumber',       // 页数
  'scenePlot',              // 剧情
  'sceneLocation',          // 拍摄地点
  'scenePlotSummary',       // 日历摘要
] as const;

/**
 * 构建 Tab 导航地图
 */
export function buildTabNavigationMap(
  scenes: SceneWithUserNames[],
  selectedSceneId: string | null,
  elements: ElementWithUserNames[],
  columns: Array<{ key: string }>
): TabNavigationItem[] {
  const map: TabNavigationItem[] = [];

  if (!selectedSceneId) return map;

  const selectedSceneIndex = scenes.findIndex(s => s.sceneID === selectedSceneId);
  if (selectedSceneIndex === -1) return map;

  // 1. ScenesTable 选中行的单元格（按列顺序）
  columns.forEach((col, colIndex) => {
    map.push({
      position: {
        type: 'scenesTable',
        sceneID: selectedSceneId,
        row: selectedSceneIndex,
        col: colIndex,
        colKey: col.key,
      },
      colKey: col.key,
    });
  });

  // 2. SceneDetails 的字段（按指定顺序）
  SCENE_DETAILS_FIELD_ORDER.forEach((field) => {
    map.push({
      position: {
        type: 'sceneDetails',
        sceneID: selectedSceneId,
        field,
      },
    });
  });

  // 3. ElementTable 的输入框（如果非空，按从左到右、从上到下顺序）
  if (elements.length > 0) {
    elements.forEach((item) => {
      // 名称输入框
      map.push({
        position: {
          type: 'elementTable',
          elementID: item.elementID,
          field: 'name',
        },
      });

      // 数量输入框（如果有）
      const isExtras = item.elementType === ElementType.BACKGROUND;
      const isFeaturedRole = item.elementType === ElementType.FEATURED;
      const isActionActor = item.elementType === ElementType.STUNT_PLAYERS;
      const isAnimal = item.elementType === ElementType.ANIMALS;
      const isVehicle = item.elementType === ElementType.VEHICLES;
      const showQuantity = isExtras || isFeaturedRole || isActionActor || isAnimal || isVehicle;

      if (showQuantity) {
        map.push({
          position: {
            type: 'elementTable',
            elementID: item.elementID,
            field: 'quantity',
          },
        });
      }
    });
  }

  return map;
}

/**
 * 构建从排期单元格 Tab 的特殊导航地图（跳过 SceneDetails 字段，直接到日历摘要）
 * 
 * 导航路径：
 * 1. ScenesTable 选中行的单元格（按列顺序）
 * 2. 直接跳转到日历摘要（跳过其他 SceneDetails 字段）
 * 3. ElementTable 的输入框（如果非空，按从左到右、从上到下顺序）
 */
export function buildTabNavigationMapFromPeriod(
  scenes: SceneWithUserNames[],
  selectedSceneId: string | null,
  elements: ElementWithUserNames[],
  columns: Array<{ key: string }>
): TabNavigationItem[] {
  const map: TabNavigationItem[] = [];

  if (!selectedSceneId) return map;

  const selectedSceneIndex = scenes.findIndex(s => s.sceneID === selectedSceneId);
  if (selectedSceneIndex === -1) return map;

  // 1. ScenesTable 选中行的单元格（按列顺序）
  columns.forEach((col, colIndex) => {
    map.push({
      position: {
        type: 'scenesTable',
        sceneID: selectedSceneId,
        row: selectedSceneIndex,
        col: colIndex,
        colKey: col.key,
      },
      colKey: col.key,
    });
  });

  // 2. 直接跳转到日历摘要（跳过其他 SceneDetails 字段）
  map.push({
    position: {
      type: 'sceneDetails',
      sceneID: selectedSceneId,
      field: 'scenePlotSummary',
    },
  });

  // 3. ElementTable 的输入框（如果非空，按从左到右、从上到下顺序）
  if (elements.length > 0) {
    elements.forEach((item) => {
      // 名称输入框
      map.push({
        position: {
          type: 'elementTable',
          elementID: item.elementID,
          field: 'name',
        },
      });

      // 数量输入框（如果有）
      const isExtras = item.elementType === ElementType.BACKGROUND;
      const isFeaturedRole = item.elementType === ElementType.FEATURED;
      const isActionActor = item.elementType === ElementType.STUNT_PLAYERS;
      const isAnimal = item.elementType === ElementType.ANIMALS;
      const isVehicle = item.elementType === ElementType.VEHICLES;
      const showQuantity = isExtras || isFeaturedRole || isActionActor || isAnimal || isVehicle;

      if (showQuantity) {
        map.push({
          position: {
            type: 'elementTable',
            elementID: item.elementID,
            field: 'quantity',
          },
        });
      }
    });
  }

  return map;
}

/**
 * 在 Tab 导航地图中查找位置
 */
export function findPositionInTabMap(
  position: UnifiedPosition,
  map: TabNavigationItem[]
): number {
  return map.findIndex(item => {
    const itemPos = item.position;
    if (itemPos.type !== position.type) return false;
    
    switch (position.type) {
      case 'scenesTable':
        // 排序/插入后 currentState 里的 row 可能是旧索引，场次唯一标识以 sceneID+col 为准
        return itemPos.type === 'scenesTable' &&
          itemPos.sceneID === position.sceneID &&
          itemPos.col === position.col;
      case 'sceneDetails':
        return itemPos.type === 'sceneDetails' &&
          itemPos.sceneID === position.sceneID &&
          itemPos.field === position.field;
      case 'elementTable':
        return itemPos.type === 'elementTable' &&
          itemPos.elementID === position.elementID &&
          itemPos.field === position.field;
      default:
        return false;
    }
  });
}

/**
 * 获取下一个 Tab 位置
 */
export function getNextTabPosition(
  currentPosition: UnifiedPosition,
  map: TabNavigationItem[],
  shiftKey: boolean
): UnifiedPosition | null {
  const currentIndex = findPositionInTabMap(currentPosition, map);
  if (currentIndex === -1) return null;

  let nextIndex: number;
  if (shiftKey) {
    // Shift+Tab: 上一个位置
    nextIndex = currentIndex > 0 ? currentIndex - 1 : map.length - 1;
  } else {
    // Tab: 下一个位置
    nextIndex = currentIndex < map.length - 1 ? currentIndex + 1 : 0;
  }

  return map[nextIndex]?.position || null;
}

/**
 * 获取 ScenesTable 的下一个位置（方向键导航）
 */
export function getNextScenesTablePosition(
  currentRow: number,
  currentCol: number,
  totalRows: number,
  totalCols: number,
  direction: 'up' | 'down' | 'left' | 'right'
): { row: number; col: number } | null {
  let nextRow = currentRow;
  let nextCol = currentCol;

  switch (direction) {
    case 'up':
      nextRow = currentRow > 0 ? currentRow - 1 : totalRows - 1;
      break;
    case 'down':
      nextRow = currentRow < totalRows - 1 ? currentRow + 1 : 0;
      break;
    case 'left':
      nextCol = currentCol > 0 ? currentCol - 1 : totalCols - 1;
      break;
    case 'right':
      nextCol = currentCol < totalCols - 1 ? currentCol + 1 : 0;
      break;
  }

  return { row: nextRow, col: nextCol };
}

/**
 * 判断 item 是否有 quantity 字段
 */
function hasQuantityField(item: ElementWithUserNames): boolean {
  return item.elementType === ElementType.BACKGROUND ||
    item.elementType === ElementType.FEATURED ||
    item.elementType === ElementType.STUNT_PLAYERS ||
    item.elementType === ElementType.ANIMALS ||
    item.elementType === ElementType.VEHICLES;
}

/**
 * 构建 element 的列布局映射
 * 返回 Map<elementID, { columnIndex: number, rowIndex: number }>
 */
export function buildElementLayoutMap(
  elements: ElementWithUserNames[],
  rowsPerColumn?: number
): Map<string, { columnIndex: number; rowIndex: number }> {
  const layoutMap = new Map<string, { columnIndex: number; rowIndex: number }>();
  
  if (elements.length === 0) return layoutMap;
  
  const slotCount = totalSlotsForElements(elements);
  const finalRowsPerColumn = rowsPerColumn ?? rowsPerColumnFromTotalSlots(slotCount);
  
  elements.forEach((item, index) => {
    const columnIndex = Math.floor(index / finalRowsPerColumn);
    const rowIndex = index % finalRowsPerColumn;
    
    layoutMap.set(item.elementID, {
      columnIndex: Math.min(columnIndex, 2), // 最多3列（0, 1, 2）
      rowIndex,
    });
  });
  
  return layoutMap;
}

/**
 * 构建完整的列布局（包含分类头部）
 * 返回列数组，每个列包含 element 或 categoryHeader
 */
export function buildElementColumnsLayout(
  elements: ElementWithUserNames[],
  rowsPerColumn?: number
): (ElementWithUserNames | { isCategoryHeader: true; categoryType: ElementType })[][] {
  const sorted = sortElements(elements);
  const itemsWithHeaders: (ElementWithUserNames | { isCategoryHeader: true; categoryType: ElementType })[] = [];
  let previousType: ElementType | undefined = undefined;
  
  sorted.forEach((item) => {
    if (item.elementType !== previousType) {
      itemsWithHeaders.push({
        isCategoryHeader: true,
        categoryType: item.elementType!
      });
      previousType = item.elementType;
    }
    itemsWithHeaders.push(item);
  });
  
  const finalRowsPerColumn = rowsPerColumn ?? rowsPerColumnFromTotalSlots(itemsWithHeaders.length);
  
  // 按列优先分组，最多 3 列
  const columns: (ElementWithUserNames | { isCategoryHeader: true; categoryType: ElementType })[][] = [[], [], []];
  
  itemsWithHeaders.forEach((item, index) => {
    const columnIndex = Math.floor(index / finalRowsPerColumn);
    if (columnIndex < 3) {
      columns[columnIndex].push(item);
    }
  });
  
  return columns;
}

/**
 * 查找同行的 item（在不同列中），跳过分类头部
 * @param columnsLayout 完整的列布局（包含分类头部）
 * @param layoutMap item 的列索引和行索引映射（只包含实际的 element）
 * @param elements elements 数组
 * @param currentItemID 当前 item ID
 * @param direction 方向（'left' 或 'right'）
 * @returns 目标 item ID 或 null（如果循环后还是自己，返回 null）
 */
export function findSameRowItem(
  columnsLayout: (ElementWithUserNames | { isCategoryHeader: true; categoryType: ElementType })[][],
  layoutMap: Map<string, { columnIndex: number; rowIndex: number }>,
  elements: ElementWithUserNames[],
  currentItemID: string,
  direction: 'left' | 'right'
): string | null {
  // 先找到当前 item 在当前列中是第几个实际的 item（不包括分类头部）
  let currentColumnIndex = -1;
  let currentItemIndexInColumn = -1; // 在当前列中的实际 item 索引（不包括分类头部）
  
  for (let colIdx = 0; colIdx < columnsLayout.length; colIdx++) {
    const column = columnsLayout[colIdx];
    let itemCount = 0; // 实际 item 的计数（不包括分类头部）
    
    for (let rowIdx = 0; rowIdx < column.length; rowIdx++) {
      const item = column[rowIdx];
      if (!('isCategoryHeader' in item && item.isCategoryHeader)) {
        const element = item as ElementWithUserNames;
        if (element.elementID === currentItemID) {
          currentColumnIndex = colIdx;
          currentItemIndexInColumn = itemCount;
          break;
        }
        itemCount++;
      }
    }
    if (currentColumnIndex !== -1) break;
  }
  
  if (currentColumnIndex === -1 || currentItemIndexInColumn === -1) {
    return null; // 找不到当前 item
  }
  
  const startColumnIndex = currentColumnIndex;
  
  // 确定目标列索引
  let targetColumnIndex = direction === 'left' ? currentColumnIndex - 1 : currentColumnIndex + 1;
  const maxColumns = columnsLayout.length;
  
  // 尝试查找目标列的同位置 item
  let attempts = 0;
  const maxAttempts = maxColumns; // 最多尝试所有列
  
  while (attempts < maxAttempts) {
    // 处理边界：循环到另一侧
    if (targetColumnIndex < 0) {
      targetColumnIndex = maxColumns - 1;
    } else if (targetColumnIndex >= maxColumns) {
      targetColumnIndex = 0;
    }
    
    const targetColumn = columnsLayout[targetColumnIndex];
    if (!targetColumn) {
      // 目标列不存在，尝试下一列
      targetColumnIndex = direction === 'left' ? targetColumnIndex - 1 : targetColumnIndex + 1;
      attempts++;
      continue;
    }
    
    // 在目标列中找到第 currentItemIndexInColumn 个实际的 item（不包括分类头部）
    let itemCount = 0;
    let foundItem: ElementWithUserNames | null = null;
    
    for (let rowIdx = 0; rowIdx < targetColumn.length; rowIdx++) {
      const item = targetColumn[rowIdx];
      if (!('isCategoryHeader' in item && item.isCategoryHeader)) {
        if (itemCount === currentItemIndexInColumn) {
          foundItem = item as ElementWithUserNames;
          break;
        }
        itemCount++;
      }
    }
    
    if (!foundItem) {
      // 目标列中没有足够多的 item，尝试下一列
      targetColumnIndex = direction === 'left' ? targetColumnIndex - 1 : targetColumnIndex + 1;
      attempts++;
      continue;
    }
    
    // 检查是否循环回到了自己
    if (targetColumnIndex === startColumnIndex && foundItem.elementID === currentItemID) {
      return null; // 循环后还是自己，不需要导航
    }
    
    return foundItem.elementID;
  }
  
  // 如果所有列都尝试过了还没找到，返回 null
  return null;
}

/**
 * 获取 ElementTable 的下一个位置（方向键导航）
 */
export function getNextElementTablePosition(
  elements: ElementWithUserNames[],
  currentItemIndex: number,
  currentField: 'name' | 'quantity',
  direction: 'up' | 'down' | 'left' | 'right',
  layoutMap?: Map<string, { columnIndex: number; rowIndex: number }>,
  columnsLayout?: (ElementWithUserNames | { isCategoryHeader: true; categoryType: ElementType })[][]
): { itemIndex: number; field: 'name' | 'quantity' } | null {
  if (elements.length === 0) return null;

  const slotCount = totalSlotsForElements(elements);
  const rowsPC = rowsPerColumnFromTotalSlots(slotCount);
  const itemLayoutMap = layoutMap || buildElementLayoutMap(elements, rowsPC);
  const itemColumnsLayout = columnsLayout || buildElementColumnsLayout(elements, rowsPC);

  const currentItem = elements[currentItemIndex];
  const currentItemID = currentItem.elementID;

  let nextItemIndex = currentItemIndex;
  let nextField: 'name' | 'quantity' = currentField;

  switch (direction) {
    case 'up': {
      // 上下方向：在不同 item 之间移动（跨列），支持循环
      // 如果在 quantity 字段，上一个 item 没有 quantity，则导航到 name
      
      // 找到上一个 item（按列优先顺序）
      let foundPrevItem = false;
      let searchIndex = currentItemIndex;
      const totalItems = elements.length;
      
      // 尝试查找上一个 item
      for (let i = 0; i < totalItems; i++) {
        searchIndex = (searchIndex - 1 + totalItems) % totalItems;
        const prevItem = elements[searchIndex];
        
        // 检查上一个 item 是否有 quantity
        if (hasQuantityField(prevItem)) {
          nextItemIndex = searchIndex;
          nextField = 'quantity';
          foundPrevItem = true;
          break;
        } else {
          // 如果没有 quantity，导航到 name
          nextItemIndex = searchIndex;
          nextField = 'name';
          foundPrevItem = true;
          break;
        }
      }
      
      if (!foundPrevItem) {
        return null;
      }
      break;
    }
    case 'down': {
      // 上下方向：在不同 item 之间移动（跨列），支持循环
      // 如果在 quantity 字段，下一个 item 没有 quantity，则导航到 name
      
      // 找到下一个 item（按列优先顺序）
      let foundNextItem = false;
      let searchIndex = currentItemIndex;
      const totalItems = elements.length;
      
      // 尝试查找下一个 item
      for (let i = 0; i < totalItems; i++) {
        searchIndex = (searchIndex + 1) % totalItems;
        const nextItem = elements[searchIndex];
        
        // 检查下一个 item 是否有 quantity
        if (hasQuantityField(nextItem)) {
          nextItemIndex = searchIndex;
          nextField = 'quantity';
          foundNextItem = true;
          break;
        } else {
          // 如果没有 quantity，导航到 name
          nextItemIndex = searchIndex;
          nextField = 'name';
          foundNextItem = true;
          break;
        }
      }
      
      if (!foundNextItem) {
        return null;
      }
      break;
    }
    case 'left': {
      // 左方向：quantity → name → 左侧列同行的 item 的 quantity（如有）→ 左侧列同行的 item 的 name
      
      if (currentField === 'quantity') {
        // 如果当前是 quantity，导航到当前 item 的 name
        nextField = 'name';
      } else {
        // 如果当前是 name，查找左侧列同行的 item
        const sameRowItemID = findSameRowItem(
          itemColumnsLayout,
          itemLayoutMap,
          elements,
          currentItemID,
          'left'
        );
        
        if (sameRowItemID === null) {
          // 循环后还是自己，不需要导航
          return null;
        }
        
        // 找到目标 item
        const targetItemIndex = elements.findIndex(item => item.elementID === sameRowItemID);
        if (targetItemIndex === -1) {
          return null;
        }
        
        const targetItem = elements[targetItemIndex];
        
        // 检查是否有 quantity，有则导航到 quantity，否则导航到 name
        if (hasQuantityField(targetItem)) {
          nextItemIndex = targetItemIndex;
          nextField = 'quantity';
        } else {
          nextItemIndex = targetItemIndex;
          nextField = 'name';
        }
      }
      break;
    }
    case 'right': {
      // 右方向：name → quantity（如有）→ 右侧列同行的 item 的 name
      
      if (currentField === 'name') {
        // 如果当前是 name，检查当前 item 是否有 quantity
        if (hasQuantityField(currentItem)) {
          // 有 quantity，导航到 quantity
          nextField = 'quantity';
        } else {
          // 没有 quantity，查找右侧列同行的 item
          const sameRowItemID = findSameRowItem(
            itemColumnsLayout,
            itemLayoutMap,
            elements,
            currentItemID,
            'right'
          );
          
          if (sameRowItemID === null) {
            // 循环后还是自己，不需要导航
            return null;
          }
          
          // 找到目标 item，导航到其 name
          const targetItemIndex = elements.findIndex(item => item.elementID === sameRowItemID);
          if (targetItemIndex === -1) {
            return null;
          }
          
          nextItemIndex = targetItemIndex;
          nextField = 'name';
        }
      } else {
        // 如果当前是 quantity，查找右侧列同行的 item
        const sameRowItemID = findSameRowItem(
          itemColumnsLayout,
          itemLayoutMap,
          elements,
          currentItemID,
          'right'
        );
        
        if (sameRowItemID === null) {
          // 循环后还是自己，不需要导航
          return null;
        }
        
        // 找到目标 item，导航到其 name
        const targetItemIndex = elements.findIndex(item => item.elementID === sameRowItemID);
        if (targetItemIndex === -1) {
          return null;
        }
        
        nextItemIndex = targetItemIndex;
        nextField = 'name';
      }
      break;
    }
  }

  return { itemIndex: nextItemIndex, field: nextField };
}

/**
 * 使用导航地图的 Hook
 */
export function useNavigationMap(
  scenes: SceneWithUserNames[],
  selectedSceneId: string | null,
  elements: ElementWithUserNames[],
  columns: Array<{ key: string }>
) {
  const tabNavigationMap = useMemo(() => {
    return buildTabNavigationMap(scenes, selectedSceneId, elements, columns);
  }, [scenes, selectedSceneId, elements, columns]);

  const tabNavigationMapFromPeriod = useMemo(() => {
    return buildTabNavigationMapFromPeriod(scenes, selectedSceneId, elements, columns);
  }, [scenes, selectedSceneId, elements, columns]);

  return {
    tabNavigationMap,
    tabNavigationMapFromPeriod,
    findPositionInTabMap,
    getNextTabPosition,
    getNextScenesTablePosition,
    getNextElementTablePosition,
  };
}
